/**
 * PlayerPageHeaderView - Pure presentation component for the player page header
 *
 * Displays a breadcrumb bar alongside a + Add action bar, player identity info,
 * key indicator tiles, tab navigation, and tab content.
 * On mobile: identity block stays fixed at top, tab content scrolls independently
 * with keyboard avoidance to keep focused inputs visible above the keyboard.
 * On desktop: breadcrumb spans full width, all content flows in a single column.
 * Navigation back is handled by the breadcrumb — no close button needed.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import React, { memo } from "react";
import { Platform } from "react-native";

import { Box } from "@/components/atoms/Box";
import { KeyboardAvoidingView } from "@/components/atoms/KeyboardAvoidingView";
import { ScrollView } from "@/components/atoms/ScrollView";

import { Stack } from "@/components/atoms/Stack";

import type { PlayerPageHeaderViewProps } from "@/features/player-detail/utils/playerPageHeaderTypes";
import BreadcrumbContainer from "@/features/player-detail/components/molecules/Breadcrumb";
import IndicatorRailView from "@/features/player-detail/components/organisms/IndicatorRail/IndicatorRailView";
import MobileActionBar from "@/features/player-detail/components/organisms/MobileActionBar";
import PlayerHeroCtaCluster from "@/features/player-detail/components/organisms/PlayerHeroCtaCluster";
import PlayerPageMobileFab from "@/features/player-detail/components/organisms/PlayerPageMobileFab";
import { FAB_SAFE_AREA_CLASS } from "@/features/player-detail/components/organisms/PlayerPageMobileFab/PlayerPageMobileFabView";
import PlayerIdentityBlockView from "@/features/player-detail/components/organisms/PlayerIdentityBlock/PlayerIdentityBlockView";
import PlayerPageTabContent from "@/features/player-detail/components/organisms/PlayerPageTabContent";
import PlayerPageTabsView, {
  PlayerPageTab,
  TabPressHandlers,
} from "@/features/player-detail/components/molecules/PlayerPageTabs/PlayerPageTabsView";

import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

/** Module-level empty function for optional callback fallback */
const noop = (): void => {};

/**
 * Whether the SE-5083 floating mobile FAB should own the consumer-player CTAs.
 * True only for a loaded, non-manual player with both add-to-list and
 * add-report flows available; manual players keep the inline MobileActionBar.
 * @param props - Gating inputs
 * @param props.isManual - Whether this is a manually added player
 * @param props.playerId - The loaded player id (undefined while loading)
 * @param props.onAddToList - The add-to-list flow opener (undefined hides the FAB)
 * @param props.onAddReport - The add-report flow opener (undefined hides the FAB)
 */
const isMobileFabActive = (props: {
  isManual?: boolean;
  playerId?: string;
  onAddToList?: () => void;
  onAddReport?: () => void;
}): boolean =>
  !props.isManual &&
  Boolean(props.playerId) &&
  Boolean(props.onAddToList) &&
  Boolean(props.onAddReport);

/**
 * Extended props for PlayerPageHeaderView including tab navigation
 */
interface ExtendedPlayerPageHeaderViewProps extends PlayerPageHeaderViewProps {
  readonly activeTab: PlayerPageTab;
  readonly tabPressHandlers: TabPressHandlers;
  readonly player?:
    | import("@/generated/graphql").PlayerDetailPlayerMetaFragment
    | null;
  readonly onDownloadReport?: () => void;
  readonly isGeneratingPdf?: boolean;
  /** Whether to show the download report button (feature flag controlled) */
  readonly showDownloadReport?: boolean;
  /** Whether the screen is mobile sized */
  readonly isSmallScreen?: boolean;
  /** Callback to open the add-to-list actionsheet */
  readonly onAddToList?: () => void;
  /** Callback to navigate to report creation */
  readonly onAddReport?: () => void;
  /** Callback for "Edit player" option — manual players only */
  readonly onEditPlayer?: () => void;
  /** Callback for "Delete player" option — manual players only */
  readonly onDeletePlayer?: () => void;
  /** Callback for "Transfer reports & notes" option — admin users on manual players only */
  readonly onTransferReports?: () => void;
  /** Whether the transfer reports action is disabled (0 reports and 0 notes) */
  readonly isTransferDisabled?: boolean;
  /** Whether this is a manually added player (limits visible tabs and hides metrics) */
  readonly isManual?: boolean;
  /**
   * Whether the current user is an OWNER — gates the Financials tab
   * (SE-4348). Threaded to both the tab strip and the tab content so
   * the tab and its pane stay in sync; the tab strip hides the label
   * and the tab content falls back to overview / reportsNotes if the
   * reactive variable still holds "financials" for a non-owner.
   */
  readonly isOwner?: boolean;
  /** Whether rendered inside a modal — skips breadcrumb, uses compact spacing */
  readonly isModal?: boolean;
}

/**
 * Builds the action bar element used as the breadcrumb action slot.
 *
 * Desktop (`!isSmallScreen`) renders the SE-5022 PlayerHeroCtaCluster —
 * the new "+ Create" + "+ Add to Watchlist" cluster that owns its own
 * watchlist mutations, create-modal, and create-popover. Mobile
 * (`isSmallScreen`) keeps the existing MobileActionBar pending the
 * SE-5083 mobile FAB rework. The cluster requires a player id; if it
 * is not yet available (e.g. initial skeleton render) the slot is
 * empty so we don't render a dead button.
 * @param props - Action bar configuration props extracted from header view props
 */
const renderActionSlot = (
  props: Pick<
    ExtendedPlayerPageHeaderViewProps,
    | "isDark"
    | "onAddToList"
    | "onAddReport"
    | "onEditPlayer"
    | "onDeletePlayer"
    | "onTransferReports"
    | "isTransferDisabled"
    | "isSmallScreen"
    | "player"
  > & {
    /**
     * Whether the SE-5083 mobile FAB owns the consumer-player CTAs. When true
     * the inline MobileActionBar drops its Add-to-list / Add-report pill so the
     * two surfaces don't double up; only manual-player admin extras remain.
     */
    readonly isMobileFabActive?: boolean;
  }
): React.ReactNode => {
  if (props.isSmallScreen) {
    return (
      <MobileActionBar
        isDark={props.isDark}
        onAddToList={props.isMobileFabActive ? undefined : props.onAddToList}
        onAddReport={props.isMobileFabActive ? undefined : props.onAddReport}
        onEditPlayer={props.onEditPlayer}
        onDeletePlayer={props.onDeletePlayer}
        onTransferReports={props.onTransferReports}
        isTransferDisabled={props.isTransferDisabled}
        isSmallScreen={props.isSmallScreen}
      />
    );
  }
  if (!props.player?.id) return null;
  // Desktop manual-player management (Edit / Delete / Transfer) lives in the
  // meatball (⋯) menu on the CTA cluster. The callbacks are pre-gated upstream
  // (undefined for non-manual players / insufficient permissions), so the
  // cluster only renders the meatball when at least one is defined.
  return (
    <PlayerHeroCtaCluster
      playerId={props.player.id}
      onEditPlayer={props.onEditPlayer}
      onDeletePlayer={props.onDeletePlayer}
      onTransferReports={props.onTransferReports}
      isTransferDisabled={props.isTransferDisabled}
    />
  );
};

/**
 * PlayerPageHeaderView renders the player header layout
 * @param props - View props including identity block, indicator rail, and tab data
 * @param props.identityBlockProps - Props for player identity display
 * @param props.indicatorRailProps - Props for indicator tiles rail
 * @param props.isDark - Whether dark mode is active
 * @param props.activeTab - Currently selected tab key
 * @param props.tabPressHandlers - Pre-memoized handlers for each tab
 * @param props.player - Player data fragment for tab content
 * @param props.onDownloadReport - Callback for download report button
 * @param props.isGeneratingPdf - Whether PDF generation is in progress
 * @param props.showDownloadReport - Whether to show the download report button
 * @param props.isSmallScreen - Whether the screen is mobile sized
 * @param props.onAddToList - Callback to open the add-to-list actionsheet
 * @param props.onAddReport - Callback to navigate to report creation
 * @param props.onEditPlayer - Callback for "Edit player" (manual players only)
 * @param props.onDeletePlayer - Callback for "Delete player" (manual players only)
 * @param props.onTransferReports - Callback for "Transfer reports & notes" (admin on manual players)
 * @param props.isTransferDisabled - Whether the transfer action is disabled (0 reports and 0 notes)
 * @param props.isManual - Whether this is a manually added player (limits visible tabs and hides metrics)
 * @param props.isOwner - Whether the current user is an OWNER (gates the Financials tab, SE-4348)
 * @param props.isModal - Whether rendered inside a modal (skips breadcrumb, compact spacing)
 * @returns VStack containing breadcrumb, identity block, indicator rail, and tabs
 */
const PlayerPageHeaderView = ({
  identityBlockProps,
  indicatorRailProps,
  isDark,
  activeTab,
  tabPressHandlers,
  player,
  onDownloadReport,
  isGeneratingPdf = false,
  showDownloadReport = false,
  isSmallScreen = false,
  onAddToList,
  onAddReport,
  onEditPlayer,
  onDeletePlayer,
  onTransferReports,
  isTransferDisabled,
  isManual = false,
  isOwner = false,
  isModal = false,
}: ExtendedPlayerPageHeaderViewProps) => (
  <Stack className={`w-full ${isSmallScreen ? "flex-1" : ""}`}>
    {isSmallScreen ? (
      <>
        {/* Sticky header: breadcrumb + action bar + identity block on mobile */}
        <Box className="z-10 w-full px-5 pb-2 shadow-soft-2">
          <Stack
            direction="horizontal"
            className="items-center justify-between"
          >
            <Box className="flex-1">
              <BreadcrumbContainer />
            </Box>
            {renderActionSlot({
              isDark,
              onAddToList,
              onAddReport,
              onEditPlayer,
              onDeletePlayer,
              onTransferReports,
              isTransferDisabled,
              isSmallScreen,
              player,
              isMobileFabActive: isMobileFabActive({
                isManual,
                playerId: player?.id,
                onAddToList,
                onAddReport,
              }),
            })}
          </Stack>
          <PlayerIdentityBlockView
            {...identityBlockProps}
            isSmallScreen={isSmallScreen}
          />
          {/* SE-8547: marks the header's bottom edge for native e2e. The
              scroll content passes UNDER this header, so a control whose top
              is above this line is occluded however "visible" it reports.
              Absolute and 1px so it takes no layout and no touches. */}
          <Box
            testID={TEST_ID_PLAYER_DETAIL.STICKY_HEADER_BOTTOM}
            className="absolute inset-x-0 bottom-0 h-px pointer-events-none"
          />
        </Box>

        {/* Scrollable: metrics, tabs, and tab content */}
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          className="flex-1"
        >
          <ScrollView
            className="flex-1"
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            automaticallyAdjustKeyboardInsets
          >
            <Box className="gap-2 px-3 pb-3">
              <IndicatorRailView
                {...indicatorRailProps}
                isDark={isDark}
                isSmallScreen={isSmallScreen}
              />
              <PlayerPageTabsView
                activeTab={activeTab}
                tabPressHandlers={tabPressHandlers}
                isDark={isDark}
                onDownloadReport={onDownloadReport ?? noop}
                isGeneratingPdf={isGeneratingPdf}
                showDownloadReport={false}
                isManual={isManual}
                isOwner={isOwner}
              />
              <PlayerPageTabContent
                activeTab={activeTab}
                isDark={isDark}
                player={player}
                isManual={isManual}
                isOwner={isOwner}
              />
              {/* SE-6910: the FAB floats over this scroll view rather than
                  displacing it, so without a reserved tail the last row's
                  controls come to rest underneath it and the FAB swallows the
                  tap. Reserved only when the FAB is actually rendered. */}
              {isMobileFabActive({
                isManual,
                playerId: player?.id,
                onAddToList,
                onAddReport,
              }) ? (
                <Box
                  testID={TEST_ID_PLAYER_DETAIL.MOBILE_FAB_SAFE_AREA}
                  className={FAB_SAFE_AREA_CLASS}
                />
              ) : null}
            </Box>
          </ScrollView>
        </KeyboardAvoidingView>

        {/* SE-5083: floating bottom-right player FAB (Notion 8.6). Consumer
            players only — manual-player Edit/Delete/Transfer admin actions stay
            on the inline MobileActionBar above. Gated on a loaded player so the
            list/report flows have an id to act on. */}
        {isMobileFabActive({
          isManual,
          playerId: player?.id,
          onAddToList,
          onAddReport,
        }) &&
        onAddToList &&
        onAddReport ? (
          <PlayerPageMobileFab
            watchlistCount={player?.watchlists?.length ?? 0}
            onAddToList={onAddToList}
            onAddReport={onAddReport}
            playerId={player?.id ?? ""}
          />
        ) : null}
      </>
    ) : (
      <>
        <Box className={`w-full ${isModal ? "gap-3 px-4 py-0" : "gap-4 py-2"}`}>
          {!isModal && (
            <Stack
              direction="horizontal"
              className="items-center justify-between"
            >
              <Box className="flex-1">
                <BreadcrumbContainer />
              </Box>
              {renderActionSlot({
                isDark,
                onAddToList,
                onAddReport,
                onEditPlayer,
                onDeletePlayer,
                onTransferReports,
                isTransferDisabled,
                isSmallScreen,
                player,
              })}
            </Stack>
          )}
          {isModal && (
            <Stack direction="horizontal" className="items-center justify-end">
              {renderActionSlot({
                isDark,
                onAddToList,
                onAddReport,
                onEditPlayer,
                onDeletePlayer,
                onTransferReports,
                isTransferDisabled,
                isSmallScreen,
                player,
              })}
            </Stack>
          )}
          <PlayerIdentityBlockView
            {...identityBlockProps}
            isCompact={isModal}
          />
          <IndicatorRailView
            {...indicatorRailProps}
            isDark={isDark}
            isSmallScreen={isSmallScreen}
            isCompact={isModal}
          />
          <PlayerPageTabsView
            activeTab={activeTab}
            tabPressHandlers={tabPressHandlers}
            isDark={isDark}
            onDownloadReport={onDownloadReport ?? noop}
            isGeneratingPdf={isGeneratingPdf}
            showDownloadReport={showDownloadReport && !isSmallScreen}
            isManual={isManual}
            isOwner={isOwner}
            isCompact={isModal}
          />
        </Box>
        <PlayerPageTabContent
          activeTab={activeTab}
          isDark={isDark}
          player={player}
          isManual={isManual}
          isOwner={isOwner}
          isCompact={isModal}
        />
      </>
    )}
  </Stack>
);

PlayerPageHeaderView.displayName = "PlayerPageHeaderView";

export default memo(PlayerPageHeaderView);
